import { CdkOverlayOrigin, Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PalantirIconComponent } from '../icon/icon.component';
import type { PalantirIcon } from '../icon/icon.type';
import { PalantirTooltipModule } from '../tooltip/tooltip.module';
import { sideMenuLogger } from './logger.instance';
import { overlayPositionOnCollapsedMenu, overlayPositionOnExpandedMenu } from './side-menu.consts';
import type { SideMenuItem, SideMenuCompany, SideMenuUser } from './side-menu.type';

/**
 * Logger instance for the component
 */
const logger = sideMenuLogger.extend('component');

/**
 * Side menu component with collapsible navigation
 * Supports nested menu items, user profiles, and notifications
 *
 * @example
 * ```html
 * <palantir-sidemenu
 *   [menuItems]="items"
 *   [menuExpanded]="true"
 *   (menuItemClick)="onItemClick($event)"
 * />
 * ```
 */
@Component({
  selector: 'palantir-sidemenu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, PalantirIconComponent, OverlayModule, PalantirTooltipModule],
})
export class PalantirSideMenuComponent implements OnInit, AfterViewInit {
  public static readonly LOCAL_STORAGE_KEY = 'FLUX_CONTROL.SIDEMENU';

  /**
   * Whether the menu is expanded or collapsed. This property can have two main behaviors:
   * 1. On the first load, it may be ignored if the menu state is saved in the local storage
   * 2. After the first load this input can be used to control the menu state.
   */
  public menuExpanded = input<boolean>(true);

  /**
   * User information (image, name, email)
   */
  public user = input.required<SideMenuUser>();

  /**
   * Company information (image, name)
   */
  public company = input.required<SideMenuCompany>();

  /**
   * Whether there are company news/updates
   */
  public hasCompanyNews = input<boolean>(false);

  /**
   * Number of unread notifications
   */
  public notificationCount = input<number>(0);

  /**
   * Array of menu items to display
   */
  public menuItems = input.required<SideMenuItem[]>();

  /**
   * Whether to show the rocket button
   */
  public showRocketButton = input<boolean>(true);

  /**
   * Emitted when a menu item is clicked
   */
  public menuItemClick = output<SideMenuItem>();

  /**
   * Emitted when a user action is triggered (e.g., profile, logout)
   */
  public userActionClick = output<string>();

  /**
   * Emitted when the rocket button is clicked
   */
  public rocketButtonClick = output();

  /**
   * Emitted when the help button is clicked
   */
  public helpButtonClick = output();

  /**
   * Emitted when the notification button is clicked
   */
  public notificationButtonClick = output();

  @ViewChild('clickMenuTemplate') public clickMenuTemplate!: TemplateRef<void>;

  @ViewChild('clickUserTemplate') public clickUserTemplate!: TemplateRef<void>;

  @ViewChildren('menuItemElement', { read: ElementRef })
  public menuItemElements!: QueryList<ElementRef>;

  /**
   * Currently selected menu item
   */
  public selectedMenuItem = signal<SideMenuItem | undefined>(undefined);

  /**
   * Trigger element for the overlay
   */
  public selectedTrigger = signal<CdkOverlayOrigin | undefined>(undefined);

  /**
   * Set of expanded menu item labels
   */
  public expandedMenuItems = signal<Set<string>>(new Set<string>());

  /**
   * Child items to show in the collapsed menu overlay
   */
  public childrenItemsToShowOnCollapsedMenu = signal<SideMenuItem[]>([]);

  /**
   * Current menu expanded state
   */
  public menuExpandedState = signal<boolean>(true);

  /**
   * Announcement text for screen readers
   */
  public srAnnouncement = signal<string>('');

  /**
   * Reference to the current overlay
   */
  protected overlayRef?: OverlayRef;

  /**
   * Index for keyboard navigation
   */
  private focusedMenuItemIndex = signal<number>(-1);

  private readonly computeCompanyImage = () => {
    const company = this.company();
    if (typeof company.image === 'string') {
      return company.image;
    }

    return this.menuExpandedState() ? company.image.expanded : company.image.collapsed;
  };

  public companyImage = computed<string>(this.computeCompanyImage.bind(this));

  private _ignoreReactiveEvents = true;

  public constructor(
    private readonly overlay: Overlay,
    private readonly vcr: ViewContainerRef,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
  ) {
    logger.info('Initializing PalantirSidemenuComponent');
    effect(() => {
      const expanded = this.menuExpanded();
      logger.debug('menuExpanded input changed', { expanded });
      untracked(() => {
        if (this._ignoreReactiveEvents) {
          logger.debug('SideMenu: first load, skipping menuExpandedState update');
          return;
        }

        this.menuExpandedState.set(expanded);
        this.updateSrAnnouncement();
      });
    });

    effect(() => {
      const expandedMenuItems = this.expandedMenuItems();
      const menuExpandedState = this.menuExpandedState();
      logger.debug('SideMenu: state changed', { expandedMenuItems, menuExpandedState });
      untracked(() => {
        if (this._ignoreReactiveEvents) {
          logger.debug('SideMenu: first load, skipping save');
          return;
        }

        this.saveMenuState();
      });
    });

    this.setupRouterSubscription();
  }

  public ngOnInit(): void {
    logger.debug('ngOnInit: updating selected menu item based on current route');
    this.updateSelectedMenuItemFromRoute();

    logger.debug('ngOnInit: loading menu state');
    this.loadMenuState();
  }

  public ngAfterViewInit(): void {
    logger.debug('ngAfterViewInit: loading menu state');
    this._ignoreReactiveEvents = false;
  }

  /**
   * Opens the click menu overlay for collapsed menu items with children
   */
  public openClickMenu(event: Event, item: SideMenuItem): void {
    logger.debug('openClickMenu', { item });
    if (!item.children?.length) {
      return;
    }

    if (this.overlayRef) {
      logger.debug('openClickMenu: overlay already open, closing');
      this.closeMenuOverlay();
      return;
    }

    const target = event.currentTarget as HTMLElement;

    this.overlayRef = this.createOverlay(target, overlayPositionOnCollapsedMenu);
    this.overlayRef.attach(new TemplatePortal(this.clickMenuTemplate, this.vcr));
    logger.info('Click menu overlay opened');
    this.setupBackdropClickHandler(() => {
      logger.debug('Backdrop clicked: clearing expandedMenuItems');
      this.expandedMenuItems.set(new Set<string>());
    });
  }

  /**
   * Opens the user menu overlay
   */
  public openUserMenu(event: Event): void {
    logger.debug('openUserMenu');
    if (this.overlayRef) {
      logger.debug('openUserMenu: overlay already open, closing');
      this.closeMenuOverlay();
      return;
    }

    const target = event.currentTarget as HTMLElement;

    const positions = this.menuExpandedState()
      ? overlayPositionOnExpandedMenu
      : overlayPositionOnCollapsedMenu;

    this.overlayRef = this.createOverlay(target, positions);
    this.overlayRef.attach(new TemplatePortal(this.clickUserTemplate, this.vcr));
    logger.info('User menu overlay opened');
    this.setupBackdropClickHandler();
  }

  /**
   * Toggles the menu expanded/collapsed state
   */
  public toggleMenu(): void {
    logger.debug('toggleMenu', { currentState: this.menuExpandedState() });
    this.menuExpandedState.set(!this.menuExpandedState());
    if (this.menuExpandedState()) {
      logger.debug('toggleMenu: menu expanded, closing overlays');
      this.closeMenuOverlay();
    }

    this.updateSrAnnouncement();
  }

  /**
   * Closes the current menu overlay
   */
  public closeMenuOverlay(): void {
    if (this.overlayRef) {
      logger.debug('closeMenuOverlay: disposing overlay');
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }

    this.selectedTrigger.set(undefined);
  }

  /**
   * Toggles a submenu's expanded state
   */
  public toggleSubmenu(label: string): void {
    logger.debug('toggleSubmenu', { label });
    const currentSet = this.expandedMenuItems();
    this.childrenItemsToShowOnCollapsedMenu.set(
      this.menuItems().find((item: SideMenuItem) => item.title === label)?.children ?? [],
    );

    if (currentSet.has(label)) {
      const newSet = new Set(currentSet);
      newSet.delete(label);
      this.expandedMenuItems.set(newSet);
      logger.debug('toggleSubmenu: collapsed', { label });
    } else {
      const newSet = new Set(currentSet);
      newSet.add(label);
      this.expandedMenuItems.set(newSet);
      logger.debug('toggleSubmenu: expanded', { label });
    }
  }

  /**
   * Checks if a submenu is expanded
   */
  public isSubmenuExpanded(label: string): boolean {
    return this.expandedMenuItems().has(label);
  }

  /**
   * Checks if a menu item is the active route
   */
  public isActiveRoute(item: SideMenuItem): boolean {
    if (!item.url) {
      return false;
    }

    try {
      return this.router.isActive(item.url, {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      });
    } catch (error) {
      logger.warn('isActiveRoute: Router check failed', { item, error });
      // Router might not be available in all contexts (e.g., Storybook)
      return false;
    }
  }

  /**
   * Navigates to a menu item's route
   */
  public navigateToRoute(item: SideMenuItem, event?: Event): void {
    logger.debug('navigateToRoute', { item });
    if (event) {
      event.preventDefault();
    }

    this.selectedMenuItem.set(item);
    logger.info('navigateToRoute: navigating', { item });
    this.closeMenuOverlay();

    this.menuItemClick.emit(item);
  }

  /**
   * Gets the appropriate icon for a menu item based on selection state
   */
  public getMenuItemIcon(item: SideMenuItem): PalantirIcon {
    const selectedItem = this.selectedMenuItem();
    const isActive = this.isActiveRoute(item);

    if (
      ((selectedItem?.id === item.id || isActive) && item.icon.selected) ||
      (item.icon.selected && this.isSubmenuExpanded(item.title))
    ) {
      return item.icon.selected;
    }

    return item.icon.unselected;
  }

  /**
   * Gets the appropriate icon for a child menu item based on selection state
   */
  public getChildItemIcon(child: SideMenuItem): PalantirIcon {
    const selectedItem = this.selectedMenuItem();
    const isActive = this.isActiveRoute(child);

    if ((selectedItem?.id === child.id || isActive) && child.icon.selected) {
      return child.icon.selected;
    }

    return child.icon.unselected;
  }

  /**
   * Handles user action clicks (profile, logout, etc.)
   */
  public handleUserAction(action: string): void {
    logger.info('handleUserAction', { action });
    this.closeMenuOverlay();
    this.userActionClick.emit(action);
  }

  /**
   * Safely checks if a menu item has children
   */
  public hasChildrenSafe(item: SideMenuItem): boolean {
    return Boolean(item?.children?.length);
  }

  /**
   * Safely checks if all children are hidden, if the item has no children, returns false
   */
  public areAllChildrenHidden(item: SideMenuItem): boolean {
    logger.debug('areAllChildrenHidden', { item });
    if (!this.hasChildrenSafe(item)) {
      return false;
    }

    return item?.children?.every((child) => child.hidden) ?? false;
  }

  /**
   * Handles rocket button click
   */
  public onRocketButtonClick(): void {
    logger.debug('onRocketButtonClick');
    this.rocketButtonClick.emit();
  }

  /**
   * Handles help button click
   */
  public onHelpButtonClick(): void {
    logger.debug('onHelpButtonClick');
    this.helpButtonClick.emit();
  }

  /**
   * Handles notification button click
   */
  public onNotificationButtonClick(): void {
    logger.debug('onNotificationButtonClick');
    this.notificationButtonClick.emit();
  }

  /**
   * Handles keyboard navigation within menu items
   */
  public onMenuKeyDown(event: KeyboardEvent, index: number): void {
    const visibleItems = this.menuItems().filter((item) => !item.hidden);

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const nextIdx = Math.min(index + 1, visibleItems.length - 1);
        logger.debug('onMenuKeyDown: ArrowDown', { from: index, to: nextIdx });
        this.focusMenuItem(nextIdx);
        break;
      }

      case 'ArrowUp': {
        event.preventDefault();
        const prevIdx = Math.max(index - 1, 0);
        logger.debug('onMenuKeyDown: ArrowUp', { from: index, to: prevIdx });
        this.focusMenuItem(prevIdx);
        break;
      }

      case 'ArrowRight': {
        event.preventDefault();
        const item = visibleItems[index];
        if (item?.children?.length && !this.isSubmenuExpanded(item.title)) {
          logger.debug('onMenuKeyDown: ArrowRight: expanding submenu', { title: item.title });
          this.toggleSubmenu(item.title);
        }

        break;
      }

      case 'ArrowLeft': {
        event.preventDefault();
        const currentItem = visibleItems[index];
        if (currentItem?.children?.length && this.isSubmenuExpanded(currentItem.title)) {
          logger.debug('onMenuKeyDown: ArrowLeft: collapsing submenu', {
            title: currentItem.title,
          });
          this.toggleSubmenu(currentItem.title);
        }

        break;
      }

      case 'Home':
        event.preventDefault();
        logger.debug('onMenuKeyDown: Home');
        this.focusMenuItem(0);
        break;
      case 'End':
        event.preventDefault();
        logger.debug('onMenuKeyDown: End');
        this.focusMenuItem(visibleItems.length - 1);
        break;
    }
  }

  /**
   * Creates an overlay with the specified positions
   */
  private createOverlay(target: HTMLElement, positions: any[]): OverlayRef {
    logger.debug('createOverlay', { target, positions });
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(target)
      .withPositions(positions)
      .withPush(true)
      .withViewportMargin(8);

    return this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
  }

  /**
   * Sets up the backdrop click handler for the overlay
   */
  private setupBackdropClickHandler(beforeClose?: () => void): void {
    if (!this.overlayRef) {
      return;
    }

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        logger.debug('Backdrop click detected');
        beforeClose?.();
        this.closeMenuOverlay();
      });
  }

  /**
   * Focuses a menu item by index
   */
  private focusMenuItem(index: number): void {
    logger.debug('focusMenuItem', { index });
    this.focusedMenuItemIndex.set(index);
    const elements = this.menuItemElements.toArray();
    if (elements[index]) {
      elements[index].nativeElement.focus();
    }
  }

  /**
   * Updates the screen reader announcement
   */
  private updateSrAnnouncement(): void {
    const announcement = this.menuExpandedState() ? 'Menu expandido' : 'Menu recolhido';
    logger.debug('updateSrAnnouncement', { announcement });
    this.srAnnouncement.set(announcement);
  }

  private loadMenuState(): void {
    const savedState = localStorage.getItem(PalantirSideMenuComponent.LOCAL_STORAGE_KEY);
    if (!savedState) {
      this._ignoreReactiveEvents = false;
      return;
    }

    try {
      const parsedState = JSON.parse(savedState || '{}');

      if (typeof parsedState.menuExpandedState === 'boolean') {
        this.menuExpandedState.set(parsedState.menuExpandedState);
      }

      if (Array.isArray(parsedState.expandedMenuItems)) {
        this.expandedMenuItems.set(
          new Set(parsedState.expandedMenuItems.filter((item: any) => typeof item === 'string')),
        );
      }

      logger.info('Menu state loaded', { parsedState });
    } catch (error) {
      logger.error('Error loading menu state', error);
      this._ignoreReactiveEvents = false;
    }
  }

  private saveMenuState(): void {
    if (this._ignoreReactiveEvents) {
      return;
    }

    localStorage.setItem(
      PalantirSideMenuComponent.LOCAL_STORAGE_KEY,
      JSON.stringify({
        menuExpandedState: this.menuExpandedState(),
        expandedMenuItems: Array.from(this.expandedMenuItems()),
      }),
    );
    logger.info('Menu state saved', {
      menuExpandedState: this.menuExpandedState(),
      expandedMenuItems: Array.from(this.expandedMenuItems()),
    });
  }

  /**
   * Sets up router event subscription to update selected menu item on route changes
   */
  private setupRouterSubscription(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        logger.debug('Route changed, updating selected menu item');
        this.updateSelectedMenuItemFromRoute();
      });
  }

  /**
   * Updates the selected menu item based on the current route
   */
  private updateSelectedMenuItemFromRoute(): void {
    const findActiveItem = (items: SideMenuItem[]): SideMenuItem | undefined => {
      for (const item of items) {
        if (this.isActiveRoute(item)) {
          return item;
        }

        if (item.children?.length) {
          const activeChild = findActiveItem(item.children);
          if (activeChild) {
            return activeChild;
          }
        }
      }

      return undefined;
    };

    const activeItem = findActiveItem(this.menuItems());
    if (activeItem) {
      logger.debug('updateSelectedMenuItemFromRoute: found active item', { activeItem });
      this.selectedMenuItem.set(activeItem);
    } else {
      this.selectedMenuItem.set(undefined);
      logger.debug('updateSelectedMenuItemFromRoute: no active item found');
    }
  }
}
